/**
 * `useCandidateTableRows` — joins `useCandidates()` + `useRanking({ jdId })`,
 * applies the active filters (status + name substring), and sorts by the
 * caller-supplied (sortKey, sortDir) with the terminal-sink invariant.
 *
 * The hook is a pure useMemo over the two query results' `data`; it does NOT
 * fetch. The caller (page) owns the queries and passes the JD id explicitly.
 *
 * See design §2.2 (join logic), §4.1 (terminal sink), §4.2 (sort keys).
 */
import { useMemo } from "react";
import type { Candidate } from "@/hooks/queries/candidates/useCandidates";
import type { TenureSignal } from "@/hooks/queries/candidates/useRanking";
import type { FitOverall } from "@/lib/fitScore";
import type { CandidateStatus } from "@/components/candidates/StatusPill";
import {
  type CandidateTableRow,
  type SortDir,
  type SortKey,
  FIT_TIER_ORDER,
  STATUS_SORT_ORDER,
  TENURE_TIER_ORDER,
  TERMINAL_STATUSES,
} from "./types";

interface RankingRow {
  candidate_id: string;
  rank_position: number;
  summary: string;
  tenure_signal: TenureSignal;
  fit_overall: FitOverall;
  /** ranking.system v3: stored 0–100 score; null for legacy rows. */
  fit_score: number | null;
  /** JD-anchored, evidence-gated skill tags (≤6 per ranking). Forward-only — legacy rows return []. */
  skills_tags: string[];
}

interface UseCandidateTableRowsOpts {
  candidates: Candidate[] | undefined;
  rankings: RankingRow[] | null | undefined;
  filterStatus: CandidateStatus | null;
  filterQ: string;
  sortKey: SortKey;
  sortDir: SortDir;
}

/**
 * Compare by primary sort key. Returns negative if a < b, positive if a > b.
 * Terminal-sink runs *before* this — see `sortRows` below.
 *
 * Direction is applied at the call site (sortRows multiplies by ±1).
 */
function compareByKey(
  a: CandidateTableRow,
  b: CandidateTableRow,
  sortKey: SortKey,
): number {
  switch (sortKey) {
    case "rank": {
      // Ranked rows (rank_position != null) before unranked.
      const aR = a.rank_position;
      const bR = b.rank_position;
      if (aR == null && bR == null) return 0;
      if (aR == null) return 1;
      if (bR == null) return -1;
      return aR - bR;
    }
    case "name":
      return a.name.localeCompare(b.name);
    case "status":
      return STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status];
    case "fit": {
      // Tiered: strong > moderate > weak > null. Within tier, by rank asc.
      const aT = a.fit_overall == null ? 99 : FIT_TIER_ORDER[a.fit_overall];
      const bT = b.fit_overall == null ? 99 : FIT_TIER_ORDER[b.fit_overall];
      if (aT !== bT) return aT - bT;
      const aR = a.rank_position ?? Number.MAX_SAFE_INTEGER;
      const bR = b.rank_position ?? Number.MAX_SAFE_INTEGER;
      return aR - bR;
    }
    case "tenure": {
      const aT =
        a.tenure_signal == null ? 99 : TENURE_TIER_ORDER[a.tenure_signal];
      const bT =
        b.tenure_signal == null ? 99 : TENURE_TIER_ORDER[b.tenure_signal];
      if (aT !== bT) return aT - bT;
      const aR = a.rank_position ?? Number.MAX_SAFE_INTEGER;
      const bR = b.rank_position ?? Number.MAX_SAFE_INTEGER;
      return aR - bR;
    }
  }
}

/**
 * Sort rows with the terminal-sink invariant. Terminal candidates (opted_out,
 * rejected) ALWAYS sit at the bottom regardless of sortKey × sortDir — the
 * descending-sort flip does NOT lift them to the top. See design §4.1.
 *
 * Exported for direct unit-testing (covers all 5×2 sort cases).
 */
export function sortRows(
  rows: CandidateTableRow[],
  sortKey: SortKey,
  sortDir: SortDir,
): CandidateTableRow[] {
  return [...rows].sort((a, b) => {
    // 1. TERMINAL SINK — always first, regardless of sortKey/sortDir.
    const aTerminal = TERMINAL_STATUSES.has(a.status);
    const bTerminal = TERMINAL_STATUSES.has(b.status);
    if (aTerminal !== bTerminal) return aTerminal ? 1 : -1;

    // 2. Primary sort key.
    const cmp = compareByKey(a, b, sortKey);

    // 3. Apply direction to the primary key (NOT to the terminal sink).
    const directed = sortDir === "asc" ? cmp : -cmp;

    // 4. Stable tiebreak: name ascending (same in both directions).
    if (directed === 0) return a.name.localeCompare(b.name);

    return directed;
  });
}

/**
 * Merge `Candidate` (from useCandidates) with the matching `RankingRow`
 * (from useRanking) into the joined table-row shape.
 *
 * Exported for direct unit-testing of the join-decoration step.
 */
export function joinRows(
  candidates: Candidate[],
  rankings: RankingRow[] | null | undefined,
): CandidateTableRow[] {
  const byId = new Map<string, RankingRow>();
  if (rankings) {
    for (const r of rankings) byId.set(r.candidate_id, r);
  }
  return candidates.map((c) => {
    const r = byId.get(c.id);
    return {
      id: c.id,
      name: c.name,
      status: c.status,
      filename: c.filename,
      linkedin_url: c.linkedin_url,
      is_sample: c.is_sample,
      last_activity_at: c.last_activity_at,
      rank_position: r?.rank_position ?? null,
      // D5 — HEADLINE fit is the candidate's routed-jd_id ranking (carried on
      // the candidate row), the SAME ranking the JD-centric Dashboard shows.
      // Prefer it over the selected-JD ranking decoration (`r.fit_overall`) so
      // the same candidate never shows "Weak Fit" on one screen and "Moderate"
      // on another. Fall back to the selected-JD ranking only when the backend
      // supplied no headline (e.g. candidate has no ranking yet).
      fit_overall: c.fit_overall ?? r?.fit_overall ?? null,
      tenure_signal: r?.tenure_signal ?? null,
      summary: r?.summary ?? null,
      fit_score: r?.fit_score ?? null,
      skills_tags: r?.skills_tags ?? [],
    };
  });
}

/** Apply client-side filters (status equality + case-insensitive name substring). */
function applyFilters(
  rows: CandidateTableRow[],
  filterStatus: CandidateStatus | null,
  filterQ: string,
): CandidateTableRow[] {
  const q = filterQ.trim().toLowerCase();
  if (!filterStatus && !q) return rows;
  return rows.filter((r) => {
    if (filterStatus && r.status !== filterStatus) return false;
    if (q && !r.name.toLowerCase().includes(q)) return false;
    return true;
  });
}

export function useCandidateTableRows(
  opts: UseCandidateTableRowsOpts,
): CandidateTableRow[] {
  const { candidates, rankings, filterStatus, filterQ, sortKey, sortDir } =
    opts;
  return useMemo(() => {
    const cs = candidates ?? [];
    const joined = joinRows(cs, rankings);
    const filtered = applyFilters(joined, filterStatus, filterQ);
    return sortRows(filtered, sortKey, sortDir);
  }, [candidates, rankings, filterStatus, filterQ, sortKey, sortDir]);
}
